import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, RefreshCw, Play } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { useOverrides, useUpdateOverride } from "@/hooks/useOverrides";
import { useScrapersSources } from "@/hooks/useScrapersSources";
import { OverrideFormDialog } from "@/components/overrides/OverrideFormDialog";
import { DeleteOverrideDialog } from "@/components/overrides/DeleteOverrideDialog";
import { ApplyOverrideDialog } from "@/components/overrides/ApplyOverrideDialog";
import { useUrlState } from "@/hooks/useUrlState";
import { useSessionState } from "@/hooks/useSessionState";
import type {
  OverridesUrlState,
  OverridesSessionState,
} from "@/types/pageState";
import type { OverrideListParams, VariantOverride } from "@/types/overrides";
import { formatRelativeTime } from "@/lib/utils";

const PAGE_SIZE = 200;

export default function Overrides() {
  const [urlState, setUrlState] = useUrlState<OverridesUrlState>({
    schema: {
      source: "string",
      is_active: "string",
      page: "number",
    },
    defaults: { page: 1 },
    replace: true,
  });

  const [sessionState, setSessionState] =
    useSessionState<OverridesSessionState>({
      key: "overrides-ui",
      defaultValue: {
        createDialogOpen: false,
        editOverrideId: null,
        deleteOverrideId: null,
        applyOverrideId: null,
      },
    });

  const params = useMemo<OverrideListParams>(() => {
    const p: OverrideListParams = {
      limit: PAGE_SIZE,
      offset: ((urlState.page || 1) - 1) * PAGE_SIZE,
    };
    if (urlState.source) p.source = urlState.source;
    if (urlState.is_active === "true") p.is_active = true;
    if (urlState.is_active === "false") p.is_active = false;
    return p;
  }, [urlState]);

  const { data: overrides = [], isLoading, refetch } = useOverrides(params);
  const { data: sources = [] } = useScrapersSources();
  const updateMutation = useUpdateOverride();

  const editOverride =
    overrides.find((o) => o.id === sessionState.editOverrideId) ?? null;
  const deleteOverride =
    overrides.find((o) => o.id === sessionState.deleteOverrideId) ?? null;
  const applyOverride =
    overrides.find((o) => o.id === sessionState.applyOverrideId) ?? null;

  const handleToggleActive = async (override: VariantOverride) => {
    try {
      await updateMutation.mutateAsync({
        id: override.id,
        body: { is_active: !override.is_active },
      });
      toast.success(
        override.is_active ? "Override disabled" : "Override enabled",
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update override",
      );
    }
  };

  const hasFilters = !!urlState.source || !!urlState.is_active;

  return (
    <div className="p-6 space-y-6">
      <Toaster />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Variant Overrides</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Correct scraped spec values that contain typos on source product
            pages.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            onClick={() =>
              setSessionState((prev) => ({ ...prev, createDialogOpen: true }))
            }
          >
            <Plus className="w-4 h-4 mr-1" />
            New Override
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">
            Source
          </label>
          <Select
            value={urlState.source || "all"}
            onValueChange={(v) => {
              if (v === "all") {
                setUrlState((prev) => {
                  const { source: _s, ...rest } = prev;
                  return { ...rest, page: 1 };
                });
              } else {
                setUrlState((prev) => ({ ...prev, source: v, page: 1 }));
              }
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">
            Status
          </label>
          <Select
            value={urlState.is_active || "all"}
            onValueChange={(v) => {
              if (v === "all") {
                setUrlState((prev) => {
                  const { is_active: _a, ...rest } = prev;
                  return { ...rest, page: 1 };
                });
              } else {
                setUrlState((prev) => ({ ...prev, is_active: v, page: 1 }));
              }
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {hasFilters && (
          <div className="self-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUrlState(() => ({ page: 1 }))}
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead>Ext. Variant ID</TableHead>
              <TableHead>Field</TableHead>
              <TableHead>Match → Override</TableHead>
              <TableHead>Tube Position</TableHead>
              <TableHead className="text-center">Active</TableHead>
              <TableHead className="text-right">Applied</TableHead>
              <TableHead>Last Applied</TableHead>
              <TableHead className="w-48" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : overrides.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground py-12"
                >
                  No overrides found.
                </TableCell>
              </TableRow>
            ) : (
              overrides.map((override) => (
                <TableRow
                  key={override.id}
                  className={!override.is_active ? "opacity-50" : undefined}
                >
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="uppercase text-xs font-mono"
                    >
                      {override.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {override.external_variant_id}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {override.field_name}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    <span className="text-destructive">
                      {override.match_value}
                    </span>
                    <span className="text-muted-foreground mx-1">→</span>
                    <span className="text-green-600 dark:text-green-400">
                      {override.override_value}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {override.tube_position ?? (
                      <span className="italic">any</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Checkbox
                      checked={override.is_active}
                      onCheckedChange={() => handleToggleActive(override)}
                      disabled={updateMutation.isPending}
                      aria-label={
                        override.is_active
                          ? "Disable override"
                          : "Enable override"
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {override.applied_count}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {override.last_applied_at ? (
                      formatRelativeTime(override.last_applied_at)
                    ) : (
                      <span className="italic">never</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <span
                        title={
                          override.is_active
                            ? undefined
                            : "Inactive overrides cannot be applied"
                        }
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={!override.is_active}
                          onClick={() =>
                            setSessionState((prev) => ({
                              ...prev,
                              applyOverrideId: override.id,
                            }))
                          }
                          aria-label="Apply now"
                        >
                          <Play className="w-3.5 h-3.5" />
                          Apply now
                        </Button>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() =>
                          setSessionState((prev) => ({
                            ...prev,
                            editOverrideId: override.id,
                          }))
                        }
                        aria-label="Edit override"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() =>
                          setSessionState((prev) => ({
                            ...prev,
                            deleteOverrideId: override.id,
                          }))
                        }
                        aria-label="Delete override"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isLoading && overrides.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Showing {overrides.length} override{overrides.length === 1 ? "" : "s"}
          {hasFilters && " (filtered)"}
        </p>
      )}

      <OverrideFormDialog
        open={sessionState.createDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, createDialogOpen: open }))
        }
      />

      <OverrideFormDialog
        open={sessionState.editOverrideId != null}
        onOpenChange={(open) => {
          if (!open)
            setSessionState((prev) => ({ ...prev, editOverrideId: null }));
        }}
        override={editOverride}
      />

      <DeleteOverrideDialog
        open={sessionState.deleteOverrideId != null}
        onOpenChange={(open) => {
          if (!open)
            setSessionState((prev) => ({ ...prev, deleteOverrideId: null }));
        }}
        override={deleteOverride}
      />

      <ApplyOverrideDialog
        open={sessionState.applyOverrideId != null}
        onOpenChange={(open) => {
          if (!open)
            setSessionState((prev) => ({ ...prev, applyOverrideId: null }));
        }}
        override={applyOverride}
      />
    </div>
  );
}
